import { IconFont } from '@/components/IconFont'
import { projectContext } from '@/layouts/MainLayout'
import { getProjectMembers } from '@/services/member/member'
import { Task } from '@/services/task/task.model'
import { CaretDownOutlined, CaretRightOutlined, CheckOutlined } from '@ant-design/icons'
import { useRequest } from 'ahooks'
import { Row, Button, Table, Typography } from 'antd'
import { createContext, memo, useContext, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import TaskAssign from '../components/TaskField/TaskAssign'
import TaskDate from '../components/TaskField/TaskDate'
import TaskName from '../components/TaskField/TaskName'
import TaskPriority from '../components/TaskField/TaskPriority'
import TaskStatus from '../components/TaskField/TaskStatus'
import styles from './index.module.less'
import {
  deleteTaskState,
  newTaskState,
  setTaskListState,
  setTaskState,
  updateTaskState,
  useTaskStore
} from './task-context'
import classnames from 'classnames'
import TableFieldFilter from './TableFieldFilter'
import TaskFilter, { initialTaskFilters, TaskFilters } from '../components/TaskFilter'
import { useFilterTaskTree, useRequestTaskList } from '../hooks'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import DraggableBodyRow from './DraggableBodyRow'
import { updateTaskOrder } from '../../../../services/task/task'

const { Text } = Typography

export const TaskListContext = createContext<any>({})

const ProjectTaskList = memo(function () {
  const { projectType, projectId } = useContext(projectContext)
  const taskStore = useTaskStore()
  const { dispatch, taskType, taskList, checkedFields } = taskStore
  const [taskFilters, setTaskFilters] = useState<TaskFilters>(initialTaskFilters)
  const [expandedRows, setExpandedRows] = useState<any>([])
  const { data: members = [] } = useRequest(() => getProjectMembers(projectId))
  const { loading, taskMeta, loadTaskList } = useRequestTaskList(taskType, (data) => {
    dispatch(setTaskListState(data))
  })
  const filterTaskTree = useFilterTaskTree(taskList, taskFilters)

  useEffect(() => {
    setTaskFilters(initialTaskFilters)
  }, [taskType])

  const handleUpdateTask = (data) => {
    dispatch(updateTaskState(data))
  }

  const columns = [
    {
      title: 'ID',
      dataIndex: 'num',
      key: 'num',
      width: 80,
      render: (num) => num && <span className={styles.taskNum}>#{num}</span>
      // align: 'center',
    },
    {
      title: 'Task name',
      dataIndex: 'name',
      key: 'name',
      width: '25%',
      // align: 'left',
      render: (name, record: Task.Info) => {
        if (record.id === -1) {
          return (
            <TaskName
              task={record}
              editing={true}
              onSuccess={(data) => {
                dispatch(newTaskState(data))
                dispatch(deleteTaskState(record.id))
              }}
              onCancel={() => {
                dispatch(deleteTaskState(record.id))
              }}
            />
          )
        }
        return (
          <Row justify={'space-between'} wrap={false}>
            <span className={classnames(styles.taskName, styles[`nameLevel-${record.path?.length}`])}>
              {!!record.children?.length && (
                <>
                  {expandedRows.includes(record.id) ? (
                    <CaretDownOutlined
                      onClick={() => {
                        setExpandedRows((origin) => origin.filter((item) => item !== record.id))
                      }}
                    />
                  ) : (
                    <CaretRightOutlined
                      onClick={() => {
                        setExpandedRows((origin) => [...new Set([...origin, record.id])])
                      }}
                    />
                  )}
                </>
              )}
              <Link to={String(record.id)}>{name}</Link>
            </span>

            {record.path?.length < 3 && (
              <Button
                type='text'
                size={'small'}
                onClick={() => {
                  dispatch(newTaskState({ id: -1, parentId: record.id }))
                  setExpandedRows((origin) => [...new Set([...origin, record.id])])
                }}
              >
                <IconFont type='icon-add' />
              </Button>
            )}
          </Row>
        )
      }
    },
    {
      title: 'Start',
      dataIndex: 'startDate',
      key: 'start',
      width: 150,
      sorter: (a: Task.Info, b: Task.Info) => a.startDate - b.startDate,
      render: (_, record) => <TaskDate task={record} field='startDate' onSuccess={() => loadTaskList()} />
      // align: 'center'
    },
    {
      title: 'Finish',
      dataIndex: 'endDate',
      key: 'end',
      width: 150,
      sorter: (a: Task.Info, b: Task.Info) => a.endDate - b.endDate,
      render: (_, record) => <TaskDate task={record} field='endDate' onSuccess={() => loadTaskList()} />
      //align: 'center'
    },
    {
      title: 'Assignees',
      dataIndex: 'assignees',
      key: 'assignees',
      width: 200,
      onFilter: (value, record: Task.Info) => !!record.assignees.find((item) => item.memberId === value),
      filterSearch: true,
      render: (_, record) => <TaskAssign members={members} task={record} onSuccess={handleUpdateTask} />
      // align: 'center'
    },
    {
      title: 'Completion Rate',
      dataIndex: 'completionRate',
      key: 'completionRate',
      render: (value) => value && `${value * 100}%`
      // align: 'center'
    },
    {
      title: 'Predecessors',
      dataIndex: 'predecessorTasks',
      key: 'predecessors',
      render: (preTasks) =>
        preTasks?.map((item) => (
          <span key={item.num} className={styles.taskNum}>
            #{item.num}
          </span>
        ))
      // align: 'center'
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      onFilter: (value, record: Task.Info) => record.priority === value,
      filterSearch: true,
      render: (_, record) => <TaskPriority task={record} onSuccess={handleUpdateTask} />
      // align: 'center'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 200,
      onFilter: (value, record: Task.Info) => record.status === value,
      filterSearch: true,
      render: (_, record) => <TaskStatus task={record} onSuccess={handleUpdateTask} />
      // align: 'center'
    }
  ]
  const filterColumns = columns.filter((column) => checkedFields.includes(column.key) || column.key === 'num')

  return (
    <TaskListContext.Provider value={{ columns, ...taskStore }}>
      <div className={styles.container}>
        <div className={styles.filter}>
          <div>
            <Button type='text' onClick={() => dispatch(setTaskState({ taskType: 'open' }))}>
              <Text strong>{taskMeta.openCount} task</Text>
            </Button>

            <Button type='text' onClick={() => dispatch(setTaskState({ taskType: 'closed' }))}>
              <Text type='secondary'>
                <CheckOutlined /> {taskMeta.closeCount} Closed
              </Text>
            </Button>
          </div>

          <div>
            <TaskFilter
              taskType={taskType}
              members={members}
              value={taskFilters}
              onChange={(filters) => {
                setTaskFilters(filters)
              }}
            />

            <span className={'ml-1'}>
              <TableFieldFilter />
            </span>
          </div>
        </div>
        <DndProvider backend={HTML5Backend}>
          <Table
            bordered
            size='small'
            locale={{ emptyText: <></> }}
            loading={loading}
            dataSource={filterTaskTree}
            rowKey={(record) => record.id}
            columns={filterColumns}
            pagination={false}
            components={
              taskType === 'open'
                ? {
                    body: {
                      row: DraggableBodyRow
                    }
                  }
                : {}
            }
            onRow={(record) => {
              const attr = {
                task: record,
                moveRow: async (id, data) => {
                  await updateTaskOrder(id, data)
                  loadTaskList()
                }
              }
              return attr as React.HTMLAttributes<any>
            }}
            summary={() => {
              if (taskType !== 'open') {
                return null
              }
              return (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}></Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>
                    <Button
                      type='text'
                      size={'small'}
                      icon={<IconFont type='icon-add' />}
                      onClick={() => {
                        dispatch(newTaskState({ id: -1 }))
                      }}
                    >
                      Click add task
                    </Button>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} colSpan={8}></Table.Summary.Cell>
                </Table.Summary.Row>
              )
            }}
            expandable={{
              expandIcon: () => '',
              indentSize: 0,
              expandedRowKeys: expandedRows,
              onExpandedRowsChange: (rows) => {
                setExpandedRows(rows)
              }
            }}
          />
        </DndProvider>
      </div>
    </TaskListContext.Provider>
  )
})

export default ProjectTaskList
